import { ROOM_BOUNDS, ROOM_OBSTACLES } from './layout.ts';
import type { AvatarState, MotionPlan, Vec2 } from './protocol.ts';
const STEP=.32, RADIUS=.24;
const COLS=Math.floor((ROOM_BOUNDS.maxX-ROOM_BOUNDS.minX)/STEP)+1;
const ROWS=Math.floor((ROOM_BOUNDS.maxZ-ROOM_BOUNDS.minZ)/STEP)+1;
const COUNT=COLS*ROWS;
const point=(id:number):Vec2=>({x:ROOM_BOUNDS.minX+(id%COLS)*STEP,z:ROOM_BOUNDS.minZ+Math.floor(id/COLS)*STEP});
export function isWalkable(p:Vec2,radius=RADIUS):boolean {
  if(!Number.isFinite(p.x)||!Number.isFinite(p.z)||p.x<ROOM_BOUNDS.minX+radius||p.x>ROOM_BOUNDS.maxX-radius||p.z<ROOM_BOUNDS.minZ+radius||p.z>ROOM_BOUNDS.maxZ-radius)return false;
  for(const o of ROOM_OBSTACLES)if(Math.abs(p.x-o.x)<o.w/2+radius&&Math.abs(p.z-o.z)<o.d/2+radius)return false;
  return true;
}
const walkable=Uint8Array.from({length:COUNT},(_,i)=>Number(isWalkable(point(i))));
function nearest(p:Vec2):number {
  let best=0, distance=Infinity;
  for(let i=0;i<COUNT;i++)if(walkable[i]){const q=point(i),d=(p.x-q.x)**2+(p.z-q.z)**2;if(d<distance){distance=d;best=i;}}
  return best;
}
/** Expanded AABB checks prevent paths grazing the edges of furniture. */
export function lineClear(a:Vec2,b:Vec2):boolean {
  if(!isWalkable(a)||!isWalkable(b))return false;
  for(const o of ROOM_OBSTACLES){
    let lo=0,hi=1; const mins=[o.x-o.w/2-RADIUS,o.z-o.d/2-RADIUS],maxs=[o.x+o.w/2+RADIUS,o.z+o.d/2+RADIUS];
    const start=[a.x,a.z],delta=[b.x-a.x,b.z-a.z];
    for(let k=0;k<2;k++){
      if(Math.abs(delta[k])<1e-9){if(start[k]<mins[k]||start[k]>maxs[k]){lo=2;break;}}
      else {let t1=(mins[k]-start[k])/delta[k],t2=(maxs[k]-start[k])/delta[k];if(t1>t2)[t1,t2]=[t2,t1];lo=Math.max(lo,t1);hi=Math.min(hi,t2);}
    }
    if(lo<=hi)return false;
  }
  return true;
}
/** Small fixed navigation grid; pathfinding runs at decisions, never per rendered frame. */
export function findPath(from:Vec2,to:Vec2):Vec2[]{
  if(!Number.isFinite(from.x+from.z+to.x+to.z))return [];
  const start=nearest(from),goal=nearest(to);
  const a=isWalkable(from)?{...from}:point(start),b=isWalkable(to)?{...to}:point(goal);
  if(lineClear(a,b))return [a,b];
  const g=new Float64Array(COUNT).fill(Infinity),parent=new Int32Array(COUNT).fill(-1),closed=new Uint8Array(COUNT);
  const heap:{id:number;f:number}[]=[];
  const heuristic=(id:number)=>{const p=point(id);return Math.hypot(p.x-b.x,p.z-b.z);};
  const push=(id:number,f:number)=>{let i=heap.length;heap.push({id,f});while(i){const p=(i-1)>>1;if(heap[p].f<=f)break;heap[i]=heap[p];i=p;}heap[i]={id,f};};
  const pop=()=>{const head=heap[0],tail=heap.pop()!;if(heap.length){let i=0;while(i*2+1<heap.length){let c=i*2+1;if(c+1<heap.length&&heap[c+1].f<heap[c].f)c++;if(heap[c].f>=tail.f)break;heap[i]=heap[c];i=c;}heap[i]=tail;}return head.id;};
  g[start]=0;push(start,heuristic(start));
  while(heap.length){
    const current=pop();if(closed[current])continue;closed[current]=1;if(current===goal)break;
    const cx=current%COLS,cz=Math.floor(current/COLS);
    for(let dz=-1;dz<=1;dz++)for(let dx=-1;dx<=1;dx++){
      if(!dx&&!dz)continue;const nx=cx+dx,nz=cz+dz;if(nx<0||nz<0||nx>=COLS||nz>=ROWS)continue;
      const next=nz*COLS+nx;if(!walkable[next]||closed[next])continue;
      if(dx&&dz&&(!walkable[cz*COLS+nx]||!walkable[nz*COLS+cx]))continue;
      const score=g[current]+STEP*(dx&&dz?Math.SQRT2:1);
      if(score<g[next]){g[next]=score;parent[next]=current;push(next,score+heuristic(next));}
    }
  }
  if(!closed[goal])return [];
  const reverse:Vec2[]=[];for(let id=goal;id!==-1;id=parent[id])reverse.push(point(id));
  const raw=[a,...reverse.reverse(),b],smoothed=[a];let index=0;
  while(index<raw.length-1){let next=raw.length-1;while(next>index+1&&!lineClear(raw[index],raw[next]))next--;smoothed.push(raw[next]);index=next;}
  return smoothed.filter((p,i)=>i===0||Math.hypot(p.x-smoothed[i-1].x,p.z-smoothed[i-1].z)>.001);
}
export function planMotion(from:Vec2,target:Vec2,now:number,speed:number,revision:number):MotionPlan|null {
  const points=findPath(from,target);if(points.length<2)return null;
  let distance=0;for(let i=1;i<points.length;i++)distance+=Math.hypot(points[i].x-points[i-1].x,points[i].z-points[i-1].z);
  if(distance<.03)return null;
  const safeSpeed=Math.max(.1,Math.min(5,speed));return {points,startedAt:now,speed:safeSpeed,endsAt:now+distance/safeSpeed*1000,revision};
}
export type AvatarSample={x:number;z:number;yaw:number;moving:boolean};
export function sampleAvatar(avatar:Pick<AvatarState,'position'|'motion'|'facing'>,now:number,out:AvatarSample={x:0,z:0,yaw:0,moving:false}):AvatarSample {
  out.x=avatar.position.x;out.z=avatar.position.z;out.yaw=avatar.facing;out.moving=false;
  const motion=avatar.motion;if(!motion||motion.points.length<2)return out;
  let distance=Math.max(0,(now-motion.startedAt)/1000)*motion.speed;
  const points=motion.points;
  for(let i=1;i<points.length;i++){
    const a=points[i-1],b=points[i],dx=b.x-a.x,dz=b.z-a.z,length=Math.hypot(dx,dz);
    if(length<.00001)continue;
    out.yaw=Math.atan2(dx,dz);
    if(distance<=length){const ratio=distance/length;out.x=a.x+dx*ratio;out.z=a.z+dz*ratio;out.moving=now>=motion.startedAt&&now<motion.endsAt;return out;}
    distance-=length;
  }
  out.x=points[points.length-1].x;out.z=points[points.length-1].z;return out;
}
